/**
 * Blog route manifest — the 12 launch posts (#107 / punch-list B8).
 *
 * Pure JS, no Vite-only syntax, so build tooling that runs under plain Node
 * — scripts/prerender.js, scripts/gen-sitemap.js — can import it. The
 * markdown content + parsing lives in blog-posts.js (Vite import.meta.glob).
 *
 * DAILY DRIP (B8, locked 2026-05-22). The 12 posts publish one per day, not
 * as a same-day dump — so each carries a true, distinct date and Google sees
 * a steady cadence. Each post's `publishDate` is LAUNCH_DATE + (order−1)
 * days; a post is only treated as live once its publishDate has arrived
 * (see blog-posts.js liveBlogPosts() + prerender's allowlist). `datePublished`
 * and `dateModified` in the post's BlogPosting JSON-LD are both stamped with
 * this distinct date — the drafts ship a `{{PUBLISH_DATE}}` token, never a
 * literal, exactly so it cannot go out un-stamped.
 *
 * LAUNCH_DATE is the day post #1 goes live — set it to the actual launch
 * day before the deploy that ships the blog.
 *
 * `slug` MUST match the `URL slug:` field in the matching src/content/blog/
 * *.md draft. `topic` / `readMin` / `featured` are the design-curation
 * fields from design/mockups/V1_Blog_Index.html. Order = pillar priority.
 */

export const LAUNCH_DATE = "2026-05-23";

/** Add `n` days to an ISO `YYYY-MM-DD` date, returning ISO `YYYY-MM-DD`. */
export function addDays(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Today as ISO `YYYY-MM-DD` (UTC). */
export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const RAW_ROUTES = [
  { order: 1, slug: "mediate-conflict-between-two-employees", topic: "Conflict Mediation", readMin: 6, featured: true },
  { order: 2, slug: "prepare-for-a-difficult-performance-review", topic: "Performance Reviews", readMin: 5 },
  { order: 3, slug: "hard-conversations-with-employees", topic: "Hard Conversations", readMin: 5 },
  { order: 4, slug: "will-a-new-hire-work-with-their-manager", topic: "Hiring", readMin: 5 },
  { order: 5, slug: "how-to-read-someone-before-a-meeting", topic: "Meeting Prep", readMin: 5 },
  { order: 6, slug: "interview-for-fit-not-just-the-resume", topic: "Hiring", readMin: 5 },
  { order: 7, slug: "manage-someone-whose-style-clashes-with-yours", topic: "Working Styles", readMin: 5 },
  { order: 8, slug: "run-better-one-on-ones", topic: "1:1s", readMin: 4 },
  { order: 9, slug: "alternatives-to-personality-tests", topic: "Personality Tests", readMin: 4 },
  { order: 10, slug: "how-peopleprints-is-different", topic: "How It Works", readMin: 4 },
  { order: 11, slug: "onboard-a-new-report-first-90-days", topic: "Onboarding", readMin: 4 },
  { order: 12, slug: "build-a-team-that-works-well-together", topic: "Team Building", readMin: 5 },
];

/** The manifest, each post stamped with its distinct drip publishDate. */
export const BLOG_ROUTES = RAW_ROUTES.map((r) => ({
  ...r,
  publishDate: addDays(LAUNCH_DATE, r.order - 1),
}));

/** Has this post's drip date arrived? */
export function isPostLive(publishDate, today = todayISO()) {
  return publishDate <= today;
}

/**
 * Live blog route paths as of `today` — `/blog` (always) plus `/blog/<slug>`
 * for every post whose drip date has arrived. Used by the prerender
 * allowlist + the sitemap generator so neither advertises a not-yet-dripped
 * post.
 */
export function liveBlogPaths(today = todayISO()) {
  return [
    "/blog",
    ...BLOG_ROUTES.filter((r) => isPostLive(r.publishDate, today)).map(
      (r) => `/blog/${r.slug}`,
    ),
  ];
}
